import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import PlayfulNavbar from "../components/playful/PlayfulNavbar";
import PlayfulFooter from "../components/playful/PlayfulFooter";
import LandingHero from "../components/landing/LandingHero";
import LandingMarquee from "../components/landing/LandingMarquee";
import WhyLinkLane from "../components/landing/WhyLinkLane";
import PlatformPillars from "../components/landing/PlatformPillars";
import CreatorSection from "../components/landing/CreatorSection";
import RoutingSection from "../components/landing/RoutingSection";
import AnalyticsSection from "../components/landing/AnalyticsSection";
import QRBrandSection from "../components/landing/QRBrandSection";
import DeveloperSection from "../components/landing/DeveloperSection";
import AISection from "../components/landing/AISection";
// import SocialProof from "../components/landing/SocialProof";
import FAQSection from "../components/landing/FAQSection";
import FinalCTA from "../components/landing/FinalCTA";
import Seo from "../components/Seo";
import { organizationLd, webSiteLd, softwareApplicationLd, faqPageLd } from "../lib/structuredData";
import { HOME_FAQ } from "../copy/homeFaq";
import { useAuth } from "../context/AuthContext";

// Credibility band — capability metrics + who it's for.
const STATS = [
  { value: "9-in-1", label: "Tools in one platform" },
  { value: "6", label: "Smart routing modes" },
  { value: "AI", label: "Explains every click" },
  { value: "Real-time", label: "Click analytics" },
];

function StatsStrip() {
  return (
    <section className="border-b-2 border-ink bg-white">
      <div className="mx-auto max-w-[1400px] px-5 py-7 md:px-10">
        <p className="mb-6 text-center font-mono text-[10px] uppercase tracking-[0.28em] text-muted-foreground">
          One platform for creators · marketers · teams · developers
        </p>
        <div className="grid grid-cols-2 divide-y-2 divide-ink md:grid-cols-4 md:divide-x-2 md:divide-y-0">
          {STATS.map(({ value, label }) => (
            <div
              key={label}
              className="flex flex-col gap-1 px-6 py-4 text-center md:py-1 md:text-left"
            >
              <div className="font-display text-3xl font-extrabold leading-none md:text-4xl">
                {value}
              </div>
              <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                {label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/**
 * Landing — the front door to the whole LinkLane platform.
 * Composition of focused section components (see components/landing/).
 */
export default function Landing() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  // Signed-in visitors don't need the marketing front door — send them straight
  // to their dashboard. SSR-safe: during prerender there's no cookie so `user`
  // is null and the static landing HTML is emitted unchanged.
  useEffect(() => {
    if (!loading && user) navigate("/dashboard", { replace: true });
  }, [loading, user, navigate]);

  return (
    <div className="min-h-screen overflow-x-hidden bg-paper text-ink">
      <Seo
        path="/"
        jsonLd={[organizationLd(), webSiteLd(), softwareApplicationLd(), faqPageLd(HOME_FAQ)]}
      />
      <PlayfulNavbar />
      <LandingHero />
      <StatsStrip />
      <LandingMarquee />
      <WhyLinkLane />
      <PlatformPillars />
      <div className="hidden md:block">
        <CreatorSection />
        <RoutingSection />
        <AnalyticsSection />
        <QRBrandSection />
        <DeveloperSection />
      </div>
      <AISection />
      {/* <SocialProof /> */}
      <FAQSection />
      <FinalCTA />
      <PlayfulFooter />
    </div>
  );
}
